/*
 * Copyright (C) 2025 JPMorgan Chase & Company
 * All rights reserved.
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, is not permitted.
 */


import {ExdsButton, ExdsGrid, ExdsLayout, ExdsText} from "@exds/core";
import React, {useContext, useEffect, useState} from "react";
import ButtonGroup from "../../../ButtonGroup/ButtonGroup";
import './SmartLogonPairDevice.scss';
import SmartLogOnHeader from "../SmartLogOnHeader/SmartLogOnHeader";
import InfoAboutPermissionModal from './InfoAboutPermissionModal';
import AppContext from "../../../../AppContext";
import QrContainer from "./QrComponent/QrContainer";
import {doDecrypt, doEncrypt, generateKeyPair} from "../../../E2ee/E2ee";
import {DEVICE_REGISTERED_ERROR, SOMETHING_WENT_WRONG, STEP_PERFORMED} from "../../../../constants/constants";
import {apiPost} from "../../../../service/service";
import {TuxLoadingIndicator} from "@tuxedo/react-component-library";
import Alert from "../../../Alert/Alert";
import Faye from 'faye';
import {useNavigate} from "react-router-dom";
import {addPageLoadEvent} from "../../../AdobeAnalytics/AdobeAnalytics";

export default function SmartLogonPairDevice(props) {
  const {setActiveMobileAppSetupPage, updateStepStatus, skipError, setPageName } = props;
  const navigate = useNavigate();
  const [isPermissionInfoModalOpen, setIsPermissionInfoModalOpen] = useState(false);
  const {
    sidContext,
    phoneNumberContext,
    transactionIdContext,
    firstNameContext,
    lastNameContext,
    hashContext,
    hashStepsStatusContext,
    expirationTimeContext,
    loadAA
  } = useContext(AppContext);
  const sid = sidContext.at(0);
  const phoneNumber = phoneNumberContext.at(0);
  const transactionId = transactionIdContext.at(0);
  const firstName = firstNameContext.at(0);
  const lastName = lastNameContext.at(0);
  const expirationTime = expirationTimeContext.at(0);
  const [hash, setHash] = hashContext;
  const hashStepsStatus = hashStepsStatusContext.at(0);
  const [keyPair, setKeyPair] = useState({});
  const [correlationId, setCorrelationId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sloQrUrl, setSloQrUrl] = useState('');
  const [qrExpirationTime, setQrExpirationTime] = useState('');
  const [showSloContent, setShowSloContent] = useState(true);
  const modalDetails = {
    title: 'Are you sure you want to cancel mobile app setup?',
    continueButton: 'No, let\'s continue',
    modalActionButton: 'Yes, I want to cancel',
  };
  const pageName = 'Mobile App Setup | Smart Log On Pair Device';

  useEffect(() => {
    if(loadAA) {
      setPageName(pageName);
      addPageLoadEvent(pageName, sid);
    }
  }, [loadAA, sid]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const setKeys = async () => {
      const pair = await generateKeyPair();
      setKeyPair(pair);
    };

    setKeys();
  }, []);

  async function handleDecryption(encryptedData, response) {
    if (encryptedData && encryptedData !== '') {
      return await doDecrypt(encryptedData, keyPair);
    } else {
      setError(SOMETHING_WENT_WRONG);
      setCorrelationId(response.correlationId);
    }
  }

  const subscribeToFayeUrl = (token, url) => {
    const faye = new Faye.Client(url);
    faye.subscribe(`/messages/${token}`, ()=> {
      updateStepStatus('SLO_COMPLETED', hashStepsStatus.rsaSetup, STEP_PERFORMED, hashStepsStatus.sloTest, handleSuccess);
    });
  };

  const handleSuccess = () => {
    setActiveMobileAppSetupPage('testDevice');
  };

  const registerDeviceForSLO = async () => {
    setIsLoading(true);
    apiPost('/api/smartLogOn/device/registration', {
        sid,
        phoneNumber,
        transactionId,
        firstName: await doEncrypt(firstName),
        lastName: await doEncrypt(lastName),
        expirationTime,
        rsaSetup: hashStepsStatus?.rsaSetup,
        sloSetup: hashStepsStatus?.sloSetup,
        sloTest: hashStepsStatus?.sloTest,
        hash
      }, {'PUBLIC_KEY': keyPair.publicKey})
        .then(async (response) => {
          if (response.status === 200) {
            const {registrationStatus, registrationInfo} = response.data.result;
            if (registrationStatus === "Success") {
              setError(null);
              setHash(response.data.hash);
              const qrUrl = await handleDecryption(registrationInfo.qrUrl, response);
              setSloQrUrl(qrUrl);
              const qrExpiration = await handleDecryption(registrationInfo.qrExpiration, response);
              setQrExpirationTime(qrExpiration);
              const subscriptionUrl = await handleDecryption(registrationInfo.subscriptionUrl, response);
              const subscriptionToken = await handleDecryption(registrationInfo.subscriptionUrlToken, response);
              subscribeToFayeUrl(subscriptionToken, subscriptionUrl);
            } else if (registrationStatus === "DeviceRegistered") {
              setShowSloContent(false);
              setError(DEVICE_REGISTERED_ERROR);
              setCorrelationId(response.correlationId);
            } else if (registrationStatus === "SystemFailure") {
              setError(SOMETHING_WENT_WRONG);
              setCorrelationId(response.correlationId);
            }
          } else {
            setError(SOMETHING_WENT_WRONG);
            setCorrelationId(response.correlationId);
          }
          setIsLoading(false);
        })
        .finally(() => setIsLoading(false));
    };

  useEffect(() => {
    if(Object.keys(keyPair).length > 0) {
      registerDeviceForSLO();
    }
  }, [keyPair]); // eslint-disable-line react-hooks/exhaustive-deps


  const handleFinalPageNavigation = () => {
    navigate('/finish');
  };

  return (
    <>
      {(isLoading || skipError?.isLoading) && <div className="loading-spinner-pair-device"><TuxLoadingIndicator/></div>}
      {!isLoading && !skipError.isLoading && <ExdsGrid columnSpan={8}  className='slo-pd-step-details'>
        { error && error === SOMETHING_WENT_WRONG && <Alert errorMessage={error} correlationId={correlationId} loadAA={loadAA} pageName={pageName} sid={sid}/> }
        { skipError.showError && <Alert correlationId={skipError.correlationId} loadAA={loadAA} pageName={pageName} sid={sid}/> }
        <SmartLogOnHeader/>
        <ExdsText primitive="div" className='step-intro'>
          <ExdsText className='step-number' primitive="p" decoration="bold">
            Step 2 of 3
          </ExdsText>

          <ExdsText primitive="p" className='step-title' decoration="bold">
            Setup and Pair Device
          </ExdsText>
        </ExdsText>

        { error && error === DEVICE_REGISTERED_ERROR && <div className="device-registered-alert">
            <Alert errorMessage={error} correlationId={correlationId} loadAA={loadAA} pageName={pageName} sid={sid}/>
        </div> }

        {showSloContent && <><ExdsText primitive="div" className='notice'>
          <ExdsText primitive="p" decoration="bold" className='notice-header'>
            Important:
          </ExdsText>
          <ExdsText primitive="p">
            To use Smart Log On, <b>you must grant certain permissions.</b> These include access to your camera
            and notifications. If you don't enable these permissions, Smart Log On won't work for access.
          </ExdsText>
          <ExdsButton text="More information about permissions" variant="link"
                      className='info-link'
                      onClick={() => setIsPermissionInfoModalOpen(true)} />
        </ExdsText>

        <ExdsLayout
          display="flex"
          alignItems="flex-start"
          className="scan-section"
        >
          <ExdsLayout
            display="flex"
            flexDirection="column"
            alignItems="center"
            className="qr-section"
          >
            {((sloQrUrl && qrExpirationTime) || error === SOMETHING_WENT_WRONG) && <QrContainer sloQrUrl={sloQrUrl} expirationTime={qrExpirationTime} registerDeviceForSLO={registerDeviceForSLO} error={error} /> }
          </ExdsLayout>
          <ExdsText className='scan-instr' primitive="div">
            <ol>
              <li>Once downloaded and installed, open the Smart Log On mobile app.</li>
              <li>Tap the <b>Pair Device</b> button.</li>
              <li>Scan your unique one-time QR code using Smart Log On (not your native camera app).</li>
            </ol>
          </ExdsText>
        </ExdsLayout></>
      }

        <div className="pair-device-buttons">
          <ButtonGroup
            advanceButtonContent={error && error === DEVICE_REGISTERED_ERROR ? 'Continue' : ''}
            advanceButtonAction={handleFinalPageNavigation}
            modalDetails={modalDetails}
            modalAction={handleFinalPageNavigation}
            modalButtonContent={error && error === DEVICE_REGISTERED_ERROR ? '' : 'Cancel'}
            modalButtonType='button'
            backButtonContent='Back'
            backButtonAction={() => setActiveMobileAppSetupPage('downloadSmartLogOn')}
          />
        </div>

        <InfoAboutPermissionModal isPermissionInfoModalOpen={isPermissionInfoModalOpen} setIsPermissionInfoModalOpen={setIsPermissionInfoModalOpen} />
      </ExdsGrid>
      }
    </>
  );
}
